import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useContext,
  useMemo,
} from 'react'
import { Redirect } from 'react-router-dom'
import { Icon } from '@jcu/cookbook-react'
import { mdiRefresh } from '@mdi/js'

import config from '../config'
import { useSearchHandler } from '../lib/search'
import { checkAccess } from '../lib/auth'
import { ApplicationContext } from './App'
import {
  Row,
  Col,
  Button,
  Form,
  ButtonGroup,
  FormCheck,
  Spinner,
} from 'react-bootstrap'
import { Helmet } from 'react-helmet'
import { useResultTable } from '../lib/resultTable'

const allowedRoles = ['admin']

const Admin = () => {
  // Variables
  const [stats, setStats] = useState(null)
  const [server, setServer] = useState(null)

  const { requester, alertHandler, user } = useContext(ApplicationContext)

  const { hasSearched, updateSearchTerm, performSearch, results, setResults } =
    useSearchHandler({ alertHandler, requester })
  const searchInputElement = useRef(null)

  const { request } = requester

  const isMounted = useRef(true)

  const setState = (setStateFn, ...args) => {
    if (!isMounted.current) return // We are unmounted, so we don't want to update state
    setStateFn(...args)
  }

  const loadStatsData = useCallback(async () => {
    setStats(null)
    const { data } = await request('/admin/stats')
    if (data) {
      setState(setStats, data)
    }
    return !!data
  }, [request])

  const loadAdminData = useCallback(async () => {
    const { data } = await request('/admin/info')
    if (data) {
      setState(setServer, data)
    }
    return !!data
  }, [request])

  // Effects
  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])
  useEffect(() => {
    const load = async () => {
      ;(await loadStatsData()) && (await loadAdminData())
    }
    load()
  }, [loadAdminData, loadStatsData])

  // Functions
  const handleStatsRefresh = event => {
    event.preventDefault()
    loadStatsData()
  }

  const handleSearchChange = event => {
    updateSearchTerm(event.target.value)
  }

  const handleSearch = event => {
    event.preventDefault()
    performSearch('/user/search')
  }

  const handleShowAllUsers = event => {
    event.preventDefault()
    // Search for all users
    performSearch('/user/search', {
      params: { allWithRoles: true },
      minSearchLength: 0,
    })
    // Clear any search terms or state
    searchInputElement.current.value = ''
    updateSearchTerm('')
  }

  const handleRoleChange = useCallback(
    async (event, username, role) => {
      const value = event.target.checked
      const updatedRoles = { roles: { [role]: value } }
      const { error } = await request(`/user/${username}`, {
        fetchOptions: {
          method: 'PATCH',
          body: JSON.stringify(updatedRoles),
          headers: {
            'Content-Type': 'application/json',
          },
        },
      })
      if (!error) {
        // Backend doesn't actually send a response back, only a 200 status
        // code, so check if there is no error
        setResults(currentResults => {
          const updatedState = [...currentResults]
          const index = updatedState.findIndex(
            result => result.username === username
          )
          updatedState[index].roles[role] = value
          return updatedState
        })
      } else {
        setResults([])
      }
    },
    [request, setResults]
  )

  const columns = useMemo(
    () => [
      {
        Header: 'Username',
        accessor: 'username',
        Cell: ({ value }) => <code>{value}</code>,
      },
      {
        Header: 'Personal Name',
        accessor: 'personal_name',
      },
      {
        Header: 'Family Name',
        accessor: 'family_name',
      },
      {
        Header: 'Email',
        accessor: 'email',
        Cell: ({ value }) => <a href={`mailto:${value}`}>{value}</a>,
      },
      {
        Header: 'Viewer',
        accessor: 'roles.viewer',
        sortType: (rowA, rowB /*, columnId*/) => {
          if (rowA.original.roles.viewer && !rowB.original.roles.viewer)
            return -1
          if (rowB.original.roles.viewer && !rowA.original.roles.viewer)
            return 1
          return 0
        },
        Cell: ({ value, row }) => (
          <Form.Check
            type="switch"
            id="viewer-switch"
            aria-label="Toggle Viewer Switch"
          >
            <FormCheck.Input
              aria-label="Toggle Viewer Switch"
              role="button"
              checked={value || false}
              onChange={event =>
                handleRoleChange(event, row.original.username, 'viewer')
              }
              disabled={row.original.username === user.sub}
              className="rounded-pill switch-lg"
            />
          </Form.Check>
        ),
      },
      {
        Header: 'Admin',
        accessor: 'roles.admin',
        sortType: (rowA, rowB /*, columnId*/) => {
          if (rowA.original.roles.admin && !rowB.original.roles.admin) return -1
          if (rowB.original.roles.admin && !rowA.original.roles.admin) return 1
          return 0
        },
        Cell: ({ value, row }) => (
          <Form.Check
            type="switch"
            id="admin-switch"
            aria-label="Toggle Admin Switch"
          >
            <FormCheck.Input
              aria-label="Toggle Admin Switch"
              role="button"
              checked={value || false}
              onChange={event =>
                handleRoleChange(event, row.original.username, 'admin')
              }
              disabled={row.original.username === user.sub}
              className="rounded-pill switch-lg"
            />
          </Form.Check>
        ),
      },
    ],
    [handleRoleChange, user.sub]
  )

  const { render, updateData } = useResultTable({
    initialData: results,
    columns,
  })

  useEffect(() => {
    updateData(results)
  }, [results, updateData])

  // Render
  if (!checkAccess(user, allowedRoles)) {
    return <Redirect to="/" />
  }

  let resultsListing
  if (results && results.length) {
    resultsListing = render()
  } else {
    if (hasSearched) {
      resultsListing = <p>No results found. Try another search term.</p>
    }
  }

  return (
    <>
      <Helmet>
        <title>Admin</title>
      </Helmet>
      <Row className="border-bottom pb-1 mb-3">
        <Col>
          <h1>Admin</h1>
        </Col>
      </Row>
      <Row className="border-bottom pb-1 mb-3">
        <Col md={6}>
          <h2>
            Statistics{' '}
            <Button size="sm" id="statsRefresh" onClick={handleStatsRefresh}>
              <Icon
                style={{ verticalAlign: 'middle' }}
                path={mdiRefresh}
                size="1rem"
                title="Refresh Stats"
              />
            </Button>
          </h2>
          <dl>
            <dt>ORCID iDs linked</dt>
            <dd>
              <code>
                {stats ? (
                  stats.links
                ) : (
                  <Spinner animation="border" role="status" size="sm">
                    <span className="visually-hidden">
                      Loading ORCiD iDs Linked...
                    </span>
                  </Spinner>
                )}
              </code>
            </dd>
            <dt>Token Breakdown</dt>
            <dd>
              {/* eslint-disable react/jsx-indent */}
              {!stats ? (
                <code>
                  <Spinner animation="border" role="status" size="sm">
                    <span className="visually-hidden">
                      Loading ORCiD iD Tokens Linked...
                    </span>
                  </Spinner>
                </code>
              ) : (
                <>
                  <code>{stats.tokens['/read-limited']}</code> read
                  limited-access data
                  <br />
                  <code>{stats.tokens['/person/update']}</code> update person
                  <br />
                  <code>{stats.tokens['/activities/update']}</code> update
                  activities
                </>
              )}
              {/* eslint-enable react/jsx-indent */}
            </dd>
            <dt>Token Revocations</dt>
            <dd>
              <code>
                {stats ? (
                  stats.revocations
                ) : (
                  <Spinner animation="border" role="status" size="sm">
                    <span className="visually-hidden">
                      Loading ORCiD iD Tokens Revoked...
                    </span>
                  </Spinner>
                )}
              </code>
            </dd>
          </dl>
        </Col>

        <Col md={6}>
          <h2>About this App</h2>
          <dl>
            <dt>Client Version</dt>
            <dd>
              <code>{config.app.version}</code>
            </dd>
            <dt>Client Build</dt>
            <dd>
              <code>{process.env.NODE_ENV}</code>
            </dd>
            <dt>Server Mode (Environment)</dt>
            <dd>
              <code>
                {server ? (
                  server.env.current
                ) : (
                  <Spinner animation="border" role="status" size="sm">
                    <span className="visually-hidden">
                      Loading Server Environment...
                    </span>
                  </Spinner>
                )}
              </code>
            </dd>
            <dt>ORCID Client ID</dt>
            <dd>
              <code>
                {server ? (
                  server.orcid.clientId
                ) : (
                  <Spinner animation="border" role="status" size="sm">
                    <span className="visually-hidden">
                      Loading ORCiD API Client ID...
                    </span>
                  </Spinner>
                )}
              </code>
            </dd>
            <dt>ORCID Client Secret</dt>
            <dd>
              <code>
                {server ? (
                  server.orcid.clientSecret
                ) : (
                  <Spinner animation="border" role="status" size="sm">
                    <span className="visually-hidden">
                      Loading ORCiD API Client Secret...
                    </span>
                  </Spinner>
                )}
              </code>
            </dd>
            <dt>ORCID Mode</dt>
            <dd>
              <code>
                {server ? (
                  server.orcid.sandbox ? (
                    'Sandbox'
                  ) : (
                    'Production'
                  )
                ) : (
                  <Spinner animation="border" role="status" size="sm">
                    <span className="visually-hidden">
                      Loading ORCiD API Environment...
                    </span>
                  </Spinner>
                )}
              </code>
            </dd>
          </dl>
        </Col>
      </Row>

      <Row className="border-bottom pb-3 mb-3">
        <Col>
          <h2>User Management</h2>
          <p className="lead">
            Search by name, email, login ID or organisational unit ID to find
            users to configure.
          </p>

          <Form onSubmit={handleSearch}>
            <Row xs={12} sm="auto" className="mb-2 gx-1">
              <Col md={4} className="mb-1">
                <Form.Control
                  ref={searchInputElement}
                  aria-label="Identifier Search Input"
                  type="text"
                  placeholder="Enter search term"
                  onChange={handleSearchChange}
                />
                {results.length > 0 && (
                  <div className="my-1">
                    <Form.Text>
                      {results.length} result{results.length !== 1 && 's'} found
                    </Form.Text>
                  </div>
                )}
              </Col>
              <Col sm={5} md={4}>
                <div className="d-grid">
                  <ButtonGroup>
                    <Button variant="primary" type="submit" className="me-1">
                      Search
                    </Button>
                    <Button variant="secondary" onClick={handleShowAllUsers}>
                      Show All
                    </Button>
                  </ButtonGroup>
                </div>
              </Col>
            </Row>
          </Form>

          {resultsListing}
        </Col>
      </Row>

      <Row>
        <Col>
          <h2>Token Management</h2>
          <p className="lead">
            Interface for managing API tokens is coming soon.
          </p>
        </Col>
      </Row>
    </>
  )
}

export default Admin
export { allowedRoles }
